import type { ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

/**
 * A per-card action button, e.g. "delete" or "open source". `variant`
 * controls where/when it shows:
 *  - 'toolbar' (default): part of the hover toolbar alongside move/resize,
 *    only rendered when `editable` is true.
 *  - 'corner': always visible in the card's bottom-right corner, regardless
 *    of `editable` (e.g. a "navigate to source" link a viewer can also use).
 */
export interface CardGridAction<T> {
  key: string;
  icon: ReactNode;
  label: string;
  onClick: (item: T) => void;
  color?: 'default' | 'inherit' | 'error' | 'primary' | 'secondary' | 'warning' | 'success';
  variant?: 'toolbar' | 'corner';
}

export interface CardGridProps<T> {
  items: T[];
  getId: (item: T) => number | string;
  renderCard: (item: T, index: number) => ReactNode;
  /** Number of columns in the grid. Default 4. */
  columns?: number;
  /** Fixed row height in px. Default 220 — see DASHBOARD_CARD_HEIGHT. */
  rowHeight?: number;
  /** MUI spacing unit for the gap between cards. Default 1.5. */
  gap?: number;
  /** How many columns an item spans. Default: every item spans 1. */
  getColumnSpan?: (item: T) => number;
  /** Shows the hover reorder/resize/toolbar-action chrome. Default false. */
  editable?: boolean;
  onMove?: (item: T, direction: 'before' | 'after') => void;
  onResize?: (item: T, columnSpan: number) => void;
  minSpan?: number;
  /** Default: `columns`. */
  maxSpan?: number;
  actions?: CardGridAction<T>[];
  emptyState?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Reusable "grid of cards" layout: fixed-column CSS grid where each card can
 * optionally be reordered (move before/after), resized (column span), and/or
 * carry configurable per-card action buttons. Purely presentational — the
 * caller owns the data and every mutation (onMove/onResize/action.onClick),
 * so each page can back it with a different API while sharing the same
 * layout, reorder/resize interactions, and card presentation.
 */
export function CardGrid<T>({
  items,
  getId,
  renderCard,
  columns = 4,
  rowHeight = 220,
  gap = 1.5,
  getColumnSpan,
  editable = false,
  onMove,
  onResize,
  minSpan = 1,
  maxSpan,
  actions,
  emptyState,
  sx,
}: CardGridProps<T>) {
  if (items.length === 0) return emptyState != null ? <>{emptyState}</> : null;

  const effectiveMaxSpan = maxSpan ?? columns;
  const cornerActions = (actions ?? []).filter((a) => a.variant === 'corner');
  const toolbarActions = (actions ?? []).filter((a) => a.variant !== 'corner');
  const showToolbar = editable && (!!onMove || !!onResize || toolbarActions.length > 0);

  return (
    <Box
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gridAutoRows: `${rowHeight}px`,
          gap,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {items.map((item, i) => {
        const span = getColumnSpan ? getColumnSpan(item) : 1;
        return (
          <Box
            key={getId(item)}
            sx={{
              position: 'relative',
              gridColumn: `span ${span}`,
              minWidth: 0,
              '&:hover .card-grid-toolbar': { opacity: 1 },
            }}
          >
            {renderCard(item, i)}

            {cornerActions.map((action) => (
              <Tooltip key={action.key} title={action.label}>
                <IconButton
                  size="small"
                  color={action.color}
                  onClick={() => action.onClick(item)}
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}

            {showToolbar && (
              <Stack
                direction="row"
                alignItems="center"
                className="card-grid-toolbar"
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  right: 4,
                  opacity: 0,
                  transition: 'opacity 0.15s',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'var(--radius-sm)',
                  px: 0.25,
                }}
              >
                {onMove && (
                  <>
                    <Tooltip title="Move before">
                      <span>
                        <IconButton size="small" disabled={i === 0} onClick={() => onMove(item, 'before')}>
                          <ChevronLeftIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move after">
                      <span>
                        <IconButton size="small" disabled={i === items.length - 1} onClick={() => onMove(item, 'after')}>
                          <ChevronRightIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}

                <Box sx={{ flex: 1 }} />

                {onResize && (
                  <>
                    <Tooltip title="Narrower">
                      <span>
                        <IconButton size="small" disabled={span <= minSpan} onClick={() => onResize(item, span - 1)}>
                          <RemoveIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 12, textAlign: 'center' }}>
                      {span}
                    </Typography>
                    <Tooltip title="Wider">
                      <span>
                        <IconButton size="small" disabled={span >= effectiveMaxSpan} onClick={() => onResize(item, span + 1)}>
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}

                {toolbarActions.map((action) => (
                  <Tooltip key={action.key} title={action.label}>
                    <IconButton size="small" color={action.color} onClick={() => action.onClick(item)}>
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </Stack>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
